import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  makeStyles,
  withStyles,
  Box,
  TextField as TextFieldMUI,
  Tooltip,
} from '@material-ui/core';
import { Error as ErrorIcon } from '@material-ui/icons';
import { Button, Typography } from '@redshelf/redshelf-ui';
import PropTypes from 'prop-types';

import {
  BookValuesContext,
  BookLocationValuesContext,
  UserAgentValuesContext,
} from 'contexts';
import { useKeyboardShortcut } from 'hooks';
import AriaLiveRegion from '../../AriaLiveRegion';

const useStyles = makeStyles((theme) => ({
  errorIcon: {
    width: theme.spacing(2.5),
    height: theme.spacing(2.5),
    color: '#D32F2F',
  },
  pageNumberTooltipLabel: {
    ...theme.typography.body2,
    display: 'inline',
    marginLeft: theme.spacing(1.5),
    lineHeight: theme.typography.body2.lineHeight,
    color: theme.palette.greyBlue[700],
  },
  inputGroup: {
    display: 'flex',
    alignItems: 'stretch',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#767676',
    },
    '& .MuiTextField-root': {
      margin: '4px 0 4px 4px',
    },

    '& .MuiOutlinedInput-root': {
      borderRadius: '4px 0px 0px 4px',
      [theme.breakpoints.down('xs')]: {
        borderRadius: '4px',
        width: 56,
      },
    },

    '& input': {
      width: '41px',
      height: '22px',
      padding: '7px 15px',
      textAlign: 'right',
      [theme.breakpoints.down('xs')]: {
        padding: '7px 9px 7px 6px',
      },

      '&:focus, &:focus-visible, &:focus-within, &:focus-active': {
        borderWidth: '2px 0 2px 2px',
        borderRadius: '4px 0px 0px 4px',
        borderColor: theme.palette.shelfBlue[500],
        outline: 'thin dotted',
      },
    },
  },
  goBtn: {
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
    borderRadius: '0px 4px 4px 0px',
    padding: '7.5px 12px',
    width: '45px',
    '&.MuiButton-root': {
      minWidth: '45px',
      margin: '4px 4px 4px 0',
    },
  },
}));

const PageNumberTooltip = withStyles((theme) => {
  return {
    tooltip: {
      width: theme.spacing(26.5),
      padding: theme.spacing(1),
      paddingLeft: theme.spacing(1.25),
      paddingRight: theme.spacing(0),
      marginLeft: theme.spacing(13.75),
      background:
        'linear-gradient(0deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.9)), #FF2942',
      border: '2px solid #D32F2F',
      borderRadius: 4,
      boxShadow:
        '0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px rgba(0, 0, 0, 0.14), 0px 1px 3px rgba(0, 0, 0, 0.12)',
    },
  };
})(Tooltip);

const PageNumberInput = ({
  canNavigate,
  onChooseNewLocation,
  page,
  setValueLabelDisplay,
  ...props
}) => {
  const { book } = useContext(BookValuesContext);
  const { currentBookLocation } = useContext(BookLocationValuesContext);
  const { userAgent } = useContext(UserAgentValuesContext);
  const classes = useStyles(props);

  // page number textfield state
  const [activePageLabel, setActivePageLabel] = useState(page?.label ?? '');
  const [hasBadPageLabel, setHasBadPageLabel] = useState(false);

  const pageNumberInput = useRef();

  const [ariaLiveMessage, setAriaLiveMessage] = useState('');
  const [isNavigating, setIsNavigating] = useState(false);

  useEffect(() => {
    if (page?.label) {
      setActivePageLabel(page.label);
      setHasBadPageLabel(false);
    }
  }, [page]);

  const onPageValueChange = (event) => {
    setActivePageLabel(event.target.value);
  };

  const focusPageNumberInput = useCallback(() => {
    pageNumberInput.current?.querySelector('input').select();
    // Return false to prevent default behavior (e.g., typing special characters on Mac)
    return false;
  }, []);

  // page number component functions
  const goToPageFromTextInput = async (event) => {
    event.preventDefault();

    // Prevent multiple navigation attempts
    if (isNavigating) {
      return;
    }

    const value = event.target
      .querySelector('input')
      .value.trim()
      .toLowerCase();

    // clear page label error state and aria message
    setHasBadPageLabel(false);
    setAriaLiveMessage('');

    const newPage = book.findFirstPageWithLabel(value);

    if (!newPage) {
      // We can let the user know if the page label is real or not.
      setHasBadPageLabel(true);
      focusPageNumberInput();

      return;
    }

    setIsNavigating(true);

    try {
      // Go to new page due to label change and wait for it to load completely
      await onChooseNewLocation(newPage.bookLocation, () => {
        setValueLabelDisplay('on');
        // Update aria live message after page has loaded using the actual current location
        setAriaLiveMessage(
          `page ${currentBookLocation?.page?.label} has loaded`,
        );
      });
    } finally {
      setIsNavigating(false);
    }
  };

  const optAltKey = userAgent.isMac ? 'option' : 'alt';
  useKeyboardShortcut(`${optAltKey}+p`, focusPageNumberInput, {
    event: 'keydown',
  });

  const pageNumberTooltipContent = (
    <Box display="flex">
      <ErrorIcon className={classes.errorIcon} />
      <Typography className={classes.pageNumberTooltipLabel}>
        This page does not exist.
      </Typography>
    </Box>
  );

  return (
    <form
      className={classes.inputGroup}
      onSubmit={goToPageFromTextInput}
      {...props}
    >
      <PageNumberTooltip
        open={hasBadPageLabel}
        placement="top"
        PopperProps={{ role: 'alert' }}
        style={{ marginTop: 13 }}
        title={pageNumberTooltipContent}
        id="page-tooltip"
      >
        <span></span>
      </PageNumberTooltip>
      <TextFieldMUI
        autoComplete="off" // prevents suggestion tooltip from displaying
        disabled={!canNavigate}
        error={hasBadPageLabel}
        onChange={onPageValueChange}
        onKeyUp={(event) => {
          if (event.key === 'Enter') {
            return onPageValueChange(event);
          }
        }}
        ref={pageNumberInput}
        type="text"
        value={activePageLabel}
        variant="outlined"
        id="page-input"
        InputLabelProps={{ shrink: true, error: false, focused: false }}
        inputProps={{
          'aria-invalid': hasBadPageLabel,
          ...(hasBadPageLabel ? { 'aria-describedby': 'page-tooltip' } : {}),
        }}
        label="Page"
      />
      <Button
        aria-label="go to page"
        className={classes.goBtn}
        disabled={!canNavigate || isNavigating}
        type="submit"
      >
        Go
      </Button>
      <AriaLiveRegion
        id="page-input"
        isPolite={false}
        message={ariaLiveMessage}
      />
    </form>
  );
};

PageNumberInput.propTypes = {
  canNavigate: PropTypes.bool,
  onChooseNewLocation: PropTypes.func,
  page: PropTypes.object,
  setValueLabelDisplay: PropTypes.func,
};

export default PageNumberInput;
